
import React, { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
 Sparkles, Star, ArrowRight, Filter, FileText, Briefcase, TrendingUp, 
 Users, BadgeCheck, Zap, Globe, Building2, MessageCircle, Trophy, CheckCircle2,
 XCircle, Bell, SendHorizonal
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";
import { JobMatchingSearch } from "@/components/job-matching/JobMatchingSearch";
import { JobMatchingResults } from "@/components/job-matching/JobMatchingResults";
import { PremiumFeatures } from "@/components/job-matching/PremiumFeatures";
import { useJobMatching } from "@/components/job-matching/useJobMatching";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
 Select, 
 SelectContent, 
 SelectItem, 
 SelectTrigger, 
 SelectValue 
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const AIJobMatching = () => {
 const { toast } = useToast();
 const { isPremium, user } = useUserStore();
 const { 
   isAnalyzing, 
   jobMatchResults, 
   handleFindMatches,
   savedJobs,
   toggleSaveJob,
   activeFilters,
   updateFilters,
   isSaved,
   clearResults,
   searchHistory,
   matchQuality,
   jobCategories,
   appliedJobs,
   applyToJob,
   setJobAlert
 } = useJobMatching();

 const [activeTab, setActiveTab] = useState("search");
 const [showInsights, setShowInsights] = useState(false);
 const [selectedSort, setSelectedSort] = useState("relevance");
 const [featuredAgencies, setFeaturedAgencies] = useState([]);
 const [showJobDetailsDialog, setShowJobDetailsDialog] = useState(false);
 const [selectedJob, setSelectedJob] = useState(null);

 useEffect(() => {
   setFeaturedAgencies([
     {
       name: "Digital Innovators",
       type: "Agency",
       specialty: "Full-Stack Development",
       rating: 4.9,
       projects: 85,
       skills: ['React', 'Node.js', 'AWS'],
       verified: true,
       image: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60"
     },
     {
       name: "Creative Pulse",
       type: "Agency",
       specialty: "Digital Marketing",
       rating: 4.9,
       projects: 150,
       skills: ['SEO', 'Content Strategy', 'Analytics'],
       verified: true,
       image: "https://images.unsplash.com/photo-1524230572899-a752b3835840?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60"
     },
     {
       name: "TechForward",
       type: "Company",
       specialty: "Enterprise Solutions",
       rating: 4.8,
       projects: 200,
       skills: ['Cloud Architecture', 'AI/ML', 'Security'],
       verified: true,
       image: "https://images.unsplash.com/photo-1431576901776-e539bd916ba2?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60"
     }
   ]);

   if (typeof window !== 'undefined') {
     // Track page view
   }
 }, []);

 const handleTabChange = (value) => {
   setActiveTab(value);
   // Track tab change
 };

 const handleJobSelect = useCallback((job) => {
   setSelectedJob(job);
   setShowJobDetailsDialog(true);
 }, []);

 const renderJobInsights = () => {
   if (!jobMatchResults.length) return null;
   
   return (
     <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 shadow-sm mb-6">
       <CardContent className="p-4">
         <div className="flex items-center justify-between mb-3">
           <h3 className="text-lg font-semibold text-blue-800">Job Market Insights</h3>
           <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">AI Generated</Badge>
         </div>
         
         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
           <div className="flex flex-col p-3 bg-white rounded-lg border border-blue-100">
             <div className="flex items-center mb-2">
               <TrendingUp className="w-4 h-4 text-blue-500 mr-2" />
               <span className="text-sm font-medium text-blue-700">Match Quality</span>
             </div>
             <Progress value={matchQuality || 65} className="h-2 mb-1" />
             <span className="text-xs text-blue-600">{matchQuality || 65}% alignment with your profile</span>
           </div>
           
           <div className="flex flex-col p-3 bg-white rounded-lg border border-blue-100">
             <div className="flex items-center mb-2">
               <Briefcase className="w-4 h-4 text-blue-500 mr-2" />
               <span className="text-sm font-medium text-blue-700">Top Categories</span>
             </div>
             <div className="flex flex-wrap gap-1">
               {(jobCategories || ['Software Development', 'Engineering', 'Product']).map(category => (
                 <Badge key={category} variant="secondary" className="bg-blue-50 text-blue-700 text-xs">{category}</Badge>
               ))}
             </div>
           </div>
           
           <div className="flex flex-col p-3 bg-white rounded-lg border border-blue-100">
             <div className="flex items-center mb-2">
               <FileText className="w-4 h-4 text-blue-500 mr-2" />
               <span className="text-sm font-medium text-blue-700">Resume Optimization</span>
             </div>
             <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 text-xs p-0 h-auto" 
               onClick={() => isPremium ? setShowInsights(true) : toast({
                 title: "Premium Feature",
                 description: "Upgrade to Premium to access resume optimization insights.",
               })}>
               {isPremium ? "View 8 optimization tips" : "Upgrade to view insights"}
             </Button>
           </div>
         </div>
         
         {isPremium && (
           <p className="text-sm text-blue-600 italic">
             Our AI has analyzed {jobMatchResults.length} job postings and found that highlighting your 
             {' '}<span className="font-medium">project management</span> and 
             {' '}<span className="font-medium">data analysis</span> skills could increase your match rate by 18%.
           </p>
         )}
       </CardContent>
     </Card>
   );
 };

 const renderHeroSection = () => (
   <div className="relative rounded-2xl overflow-hidden mb-8">
     <div className="absolute inset-0 bg-gradient-to-br from-sky-600 via-indigo-500 to-emerald-600" />
     <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1496307653780-42ee777d4833?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80')] mix-blend-overlay opacity-20" />
     <div className="absolute inset-0 bg-[linear-gradient(40deg,rgba(0,0,0,0.3)_0%,rgba(0,0,0,0.1)_40%)]" />
     <div className="relative z-10 space-y-4 p-8 md:p-12">
       <h1 className="text-2xl md:text-4xl font-bold text-white drop-shadow-md">
         Find Your Perfect Career Match <span className="text-amber-300">with AI</span>
       </h1>
       <p className="text-base md:text-lg text-gray-100 max-w-2xl leading-relaxed">
         Our AI-powered job matching analyzes your skills, experience, and preferences to find the perfect opportunities
       </p>
       
       <div className="flex flex-wrap gap-4 mt-4">
         <Button 
           size="lg"
           className="bg-white text-sky-600 hover:bg-gray-100 hover:shadow-lg transition-all shadow-md"
           onClick={() => document.querySelector('#search-form')?.scrollIntoView({ behavior: 'smooth' })}
         >
           Start Matching <Sparkles className="ml-2 h-5 w-5" />
         </Button>
         {!isPremium && (
           <Button 
             size="lg"
             variant="outline"
             className="border-white text-white hover:bg-white/10 shadow-md"
             onClick={() => toast({
               title: "Premium Feature",
               description: "Upgrade to Premium for enhanced AI job matching and higher match scores.",
             })}
           >
             Upgrade to Premium <Star className="ml-2 h-5 w-5" />
           </Button>
         )}
       </div>
     </div>
   </div>
 );

 const renderStatsSection = () => (
   <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
     {[
       { 
         icon: <Briefcase className="h-8 w-8 text-sky-500" />, 
         label: "Job Opportunities", 
         value: "25,000+",
         desc: "Active jobs in our database"
       },
       { 
         icon: <Zap className="h-8 w-8 text-emerald-500" />, 
         label: "Average Match Time", 
         value: "2 mins",
         desc: "From resume to perfect matches"
       },
       { 
         icon: <Users className="h-8 w-8 text-amber-500" />, 
         label: "Successful Matches", 
         value: "85%",
         desc: "Candidates find their dream jobs"
       },
     ].map((stat, index) => (
       <Card key={index} className="relative overflow-hidden border-0 shadow-xl bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300 h-full">
         <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-white/10" />
         <CardContent className="relative space-y-4 p-6">
           <div className="mx-auto w-16 h-16 flex items-center justify-center bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-inner border border-gray-100">
             {stat.icon}
           </div>
           <div className="text-center">
             <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
             <p className="text-gray-700 text-base font-medium">{stat.label}</p>
             <p className="text-gray-500 text-sm mt-1">{stat.desc}</p>
           </div>
         </CardContent>
       </Card>
     ))}
   </div>
 );

 const renderFeaturedAgencies = () => (
   <div className="mb-8">
     <div className="flex items-center justify-between mb-4">
       <h2 className="text-lg font-semibold text-gray-800">Featured Agencies & Companies</h2>
       <Button variant="link" className="text-sky-600">
         View All <ArrowRight className="ml-1 h-4 w-4" />
       </Button>
     </div>
     
     <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
       {featuredAgencies.map((agency, index) => (
         <Card key={index} className="group hover:shadow-xl transition-all duration-300 bg-white overflow-hidden border border-gray-100">
           <div className="h-32 overflow-hidden relative">
             <img 
               src={agency.image} 
               alt={agency.name} 
               className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
             />
             <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
             <div className="absolute bottom-3 left-3 flex items-center gap-2">
               <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                 agency.type === "Agency" ? "bg-sky-100 text-sky-700" :
                 agency.type === "Freelancer" ? "bg-emerald-100 text-emerald-700" :
                 "bg-indigo-100 text-indigo-700"
               }`}>
                 {agency.type}
               </span>
               {agency.verified && (
                 <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                   <BadgeCheck className="h-3 w-3" />
                   Verified
                 </span>
               )}
             </div>
           </div>
           
           <CardContent className="p-4 space-y-3">
             <div>
               <h3 className="font-semibold text-base text-gray-900">{agency.name}</h3>
               <p className="text-gray-600 text-sm">{agency.specialty}</p>
             </div>

             <div className="flex items-center gap-3 text-xs text-gray-600">
               <div className="flex items-center gap-1">
                 <Star className="h-3 w-3 text-amber-400" />
                 <span>{agency.rating}</span>
               </div>
               <div className="flex items-center gap-1">
                 <Briefcase className="h-3 w-3" />
                 <span>{agency.projects}+ Projects</span>
               </div>
             </div>

             <div className="flex flex-wrap gap-1">
               {agency.skills.map((skill, idx) => (
                 <span 
                   key={idx} 
                   className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full"
                 >
                   {skill}
                 </span>
               ))}
             </div>

             <div className="flex gap-2">
               <Button className="flex-1 text-xs py-1 h-auto bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 shadow-sm hover:shadow-md transition-all">
                 View Profile
               </Button>
               <Button variant="outline" size="icon" className="h-7 w-7 border-gray-200">
                 <MessageCircle className="h-3 w-3" />
               </Button>
             </div>
           </CardContent>
         </Card>
       ))}
     </div>
   </div>
 );

 return (
   <div className="container mx-auto px-4 py-8 animate-fade-in">
     <div className="flex items-center justify-between mb-6">
       <div>
         <h1 className="text-3xl font-bold bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">AI Job Matching</h1>
         <p className="text-gray-500 mt-1">Find your perfect career match with our AI-powered job matching</p>
       </div>
       {isPremium ? (
         <Badge variant="outline" className="bg-gradient-to-r from-amber-200 to-amber-400 text-amber-900 border-amber-500 px-3 py-1">
           <Star className="w-4 h-4 mr-1 fill-amber-500 text-amber-500" /> Premium
         </Badge>
       ) : (
         <Button variant="outline" size="sm" className="gap-1" onClick={() => toast({
           title: "Premium Feature",
           description: "Upgrade to Premium for enhanced AI job matching and higher match scores.",
         })}>
           <Sparkles className="w-4 h-4" /> Upgrade to Premium
         </Button>
       )}
     </div>
     
     {renderHeroSection()}
     
     {renderStatsSection()}
     
     <Tabs defaultValue="search" className="mb-8" onValueChange={handleTabChange}>
       <TabsList className="bg-gray-100 p-1 mb-6">
         <TabsTrigger value="search" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
           <Briefcase className="h-4 w-4 mr-2" /> Search & Match
         </TabsTrigger>
         <TabsTrigger value="saved" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
           <Star className="h-4 w-4 mr-2" /> Saved Jobs {savedJobs.length > 0 && (
             <Badge variant="secondary" className="ml-1 bg-blue-100 text-blue-700 text-xs">{savedJobs.length}</Badge>
           )}
         </TabsTrigger>
         <TabsTrigger value="applied" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
           <CheckCircle2 className="h-4 w-4 mr-2" /> Applied Jobs
         </TabsTrigger>
         {isPremium && (
           <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
             <FileText className="h-4 w-4 mr-2" /> Search History
           </TabsTrigger>
         )}
       </TabsList>
       
       <TabsContent value="search" className="space-y-8">
         <div id="search-form" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
           <div className="lg:col-span-2">
             <Card className="border-blue-100 shadow-md">
               <CardHeader className="pb-2">
                 <CardTitle className="text-xl text-blue-800">Find Your Perfect Match</CardTitle>
                 <CardDescription>Upload your resume or paste job description to find AI-matched opportunities</CardDescription>
               </CardHeader>
               <CardContent>
                 <JobMatchingSearch 
                   onSearch={handleFindMatches} 
                   isAnalyzing={isAnalyzing} 
                   onClear={clearResults}
                   activeFilters={activeFilters}
                 />
               </CardContent>
             </Card>
           </div>
           <div className="lg:col-span-1">
             <PremiumFeatures />
           </div>
         </div>
         
         {featuredAgencies.length > 0 && renderFeaturedAgencies()}
         
         {jobMatchResults.length > 0 && renderJobInsights()}
         
         {jobMatchResults.length > 0 && (
           <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
             <h2 className="text-xl font-semibold text-gray-800">
               {isAnalyzing ? "Analyzing Jobs..." : `Matched Jobs (${jobMatchResults.length})`}
             </h2>
             
             <div className="flex flex-wrap items-center gap-3">
               <TooltipProvider>
                 <Tooltip>
                   <TooltipTrigger asChild>
                     <div className="flex items-center gap-2">
                       <span className="text-sm text-gray-500">Sort by:</span>
                       <Select value={selectedSort} onValueChange={setSelectedSort}>
                         <SelectTrigger className="w-[140px] h-9 text-sm">
                           <SelectValue placeholder="Sort by..." />
                         </SelectTrigger>
                         <SelectContent>
                           <SelectItem value="relevance">Relevance</SelectItem>
                           <SelectItem value="date">Date Posted</SelectItem>
                           <SelectItem value="salary">Salary</SelectItem>
                           <SelectItem value="match">Match Score</SelectItem>
                         </SelectContent>
                       </Select>
                     </div>
                   </TooltipTrigger>
                   <TooltipContent>
                     <p>Sort jobs by different criteria</p>
                   </TooltipContent>
                 </Tooltip>
               </TooltipProvider>
               
               <div className="flex gap-2">
                 <Button 
                   variant="outline" 
                   size="sm" 
                   className="gap-1 h-9"
                   onClick={() => {
                     toast({
                       title: "Filters Applied",
                       description: "Your filters have been updated.",
                     });
                   }}
                 >
                   <Filter className="h-4 w-4" /> Filter
                 </Button>
                 
                 {jobMatchResults.length > 0 && (
                   <Button 
                     variant="ghost" 
                     size="sm"
                     className="text-gray-500 h-9"
                     onClick={clearResults}
                   >
                     Clear
                   </Button>
                 )}
               </div>
             </div>
           </div>
         )}
         
         <JobMatchingResults 
           jobs={jobMatchResults} 
           isPremium={isPremium} 
           isAnalyzing={isAnalyzing}
           savedJobs={savedJobs}
           toggleSaveJob={toggleSaveJob}
           isSaved={isSaved}
           updateFilters={updateFilters}
           onJobSelect={handleJobSelect}
         />
       </TabsContent>
       
       <TabsContent value="saved" className="space-y-6">
         {savedJobs.length > 0 ? (
           <>
             <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
               <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4">
                 <div>
                   <h3 className="text-md font-medium text-blue-700 mb-1">You have {savedJobs.length} saved jobs</h3>
                   <p className="text-sm text-blue-600">
                     {isPremium 
                       ? "Premium feature: We'll notify you of application deadlines and job status changes." 
                       : "Upgrade to Premium to get notified of application deadlines and job status changes."}
                   </p>
                 </div>
                 <Button 
                   variant="default" 
                   size="sm" 
                   className="shrink-0 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                   onClick={() => toast({
                     title: isPremium ? "Job Alerts Activated" : "Premium Feature",
                     description: isPremium 
                       ? "You'll receive notifications about these saved jobs." 
                       : "Upgrade to Premium to set job alerts.",
                   })}
                 >
                   <Bell className="h-4 w-4 mr-2" /> Set Job Alerts
                 </Button>
               </CardContent>
             </Card>
             
             <div className="flex items-center justify-between mb-4">
               <h2 className="text-xl font-semibold text-gray-800">Your Saved Jobs</h2>
               
               <div className="flex items-center gap-2">
                 <Select defaultValue="date" onValueChange={(value) => console.log(value)}>
                   <SelectTrigger className="w-[140px] h-9 text-sm">
                     <SelectValue placeholder="Sort by..." />
                   </SelectTrigger>
                   <SelectContent>
                     <SelectItem value="date">Date Saved</SelectItem>
                     <SelectItem value="match">Match Score</SelectItem>
                     <SelectItem value="deadline">Application Deadline</SelectItem>
                   </SelectContent>
                 </Select>
               </div>
             </div>
             
             <JobMatchingResults 
               jobs={jobMatchResults.filter(job => isSaved(job.id))}
               isPremium={isPremium}
               isAnalyzing={false}
               savedJobs={savedJobs}
               toggleSaveJob={toggleSaveJob}
               isSaved={isSaved}
               showActions={true}
               onJobSelect={handleJobSelect}
             />
           </>
         ) : (
           <div className="bg-gray-50 rounded-lg border border-dashed p-8 text-center">
             <div className="mx-auto w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
               <Star className="h-8 w-8 text-gray-400" />
             </div>
             <h3 className="text-xl font-medium text-gray-700 mb-2">No Saved Jobs</h3>
             <p className="text-gray-500 max-w-md mx-auto mb-6">
               You haven't saved any jobs yet. When you find jobs you're interested in, save them to revisit later.
             </p>
             <Button variant="default" className="gap-2" onClick={() => setActiveTab("search")}>
               Find Jobs <ArrowRight className="h-4 w-4" />
             </Button>
           </div>
         )}
       </TabsContent>
       
       <TabsContent value="applied" className="space-y-6">
         <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-emerald-100">
           <CardContent className="p-4">
             <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
               <div>
                 <h3 className="text-md font-medium text-emerald-700 mb-1">Your Application Journey</h3>
                 <p className="text-sm text-emerald-600">
                   Track and manage all your job applications in one place
                 </p>
               </div>
               {isPremium && (
                 <Button 
                   variant="outline" 
                   size="sm" 
                   className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                 >
                   <FileText className="h-4 w-4 mr-2" /> Generate Application Report
                 </Button>
               )}
             </div>
           </CardContent>
         </Card>
         
         {(appliedJobs?.length > 0) ? (
           <>
             <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
               {[
                 { status: "Applied", count: 12, color: "blue" },
                 { status: "Interviewing", count: 5, color: "amber" },
                 { status: "Offer", count: 2, color: "emerald" },
                 { status: "Rejected", count: 3, color: "red" }
               ].map((item, index) => (
                 <Card key={index} className={`border-${item.color}-100 hover:shadow-md transition-all`}>
                   <CardContent className="p-4 flex justify-between items-center">
                     <div>
                       <p className={`text-${item.color}-600 text-sm font-medium`}>{item.status}</p>
                       <p className="text-2xl font-bold">{item.count}</p>
                     </div>
                     <div className={`w-10 h-10 rounded-full bg-${item.color}-100 flex items-center justify-center`}>
                       {item.status === "Applied" && <SendHorizonal className={`h-5 w-5 text-${item.color}-500`} />}
                       {item.status === "Interviewing" && <Users className={`h-5 w-5 text-${item.color}-500`} />}
                       {item.status === "Offer" && <Trophy className={`h-5 w-5 text-${item.color}-500`} />}
                       {item.status === "Rejected" && <XCircle className={`h-5 w-5 text-${item.color}-500`} />}
                     </div>
                   </CardContent>
                 </Card>
               ))}
             </div>
             
             <div className="flex items-center justify-between mb-4">
               <h2 className="text-xl font-semibold text-gray-800">Application History</h2>
               
               <div className="flex items-center gap-2">
                 <Select defaultValue="recent">
                   <SelectTrigger className="w-[140px] h-9 text-sm">
                     <SelectValue placeholder="Filter by..." />
                   </SelectTrigger>
                   <SelectContent>
                     <SelectItem value="recent">Most Recent</SelectItem>
                     <SelectItem value="active">Active Applications</SelectItem>
                     <SelectItem value="all">All Applications</SelectItem>
                   </SelectContent>
                 </Select>
               </div>
             </div>
             
             <Card>
               <CardContent className="p-0">
                 <div className="divide-y">
                   {Array(5).fill(0).map((_, index) => (
                    <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                       <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                         <div className="flex gap-4 items-start">
                           <div className="h-12 w-12 flex-shrink-0 bg-gray-100 rounded-md flex items-center justify-center">
                             <Building2 className="h-6 w-6 text-gray-500" />
                           </div>
                           <div>
                             <h3 className="font-medium text-gray-900">
                               {["Senior Product Designer", "Frontend Developer", "Data Scientist", "Marketing Manager", "UX Researcher"][index]}
                             </h3>
                             <p className="text-sm text-gray-500">
                               {["Google", "Microsoft", "Apple", "Netflix", "Amazon"][index]} · {["San Francisco", "New York", "Seattle", "Remote", "London"][index]}
                             </p>
                             <div className="flex items-center gap-3 mt-1">
                               <Badge variant="outline" className={
                                 index === 0 ? "bg-blue-50 text-blue-700 border-blue-100" :
                                 index === 1 ? "bg-amber-50 text-amber-700 border-amber-100" :
                                 index === 2 ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
                                 "bg-red-50 text-red-700 border-red-100"
                               }>
                                 {index === 0 ? "Applied" : 
                                  index === 1 ? "Interviewing" : 
                                  index === 2 ? "Offer" : 
                                  "Rejected"}
                               </Badge>
                               <span className="text-xs text-gray-500">Applied on March {index + 1}, 2025</span>
                             </div>
                           </div>
                         </div>
                         <div className="flex gap-2 mt-2 md:mt-0">
                           <Button variant="outline" size="sm" className="text-gray-500 border-gray-200">
                             View Details
                           </Button>
                           {index < 2 && (
                             <Button variant="default" size="sm" className="gap-1 bg-blue-600 hover:bg-blue-700">
                               <MessageCircle className="h-4 w-4" /> Follow Up
                             </Button>
                           )}
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
               </CardContent>
             </Card>
           </>
         ) : (
           <div className="bg-gray-50 rounded-lg border border-dashed p-8 text-center">
             <div className="mx-auto w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
               <CheckCircle2 className="h-8 w-8 text-gray-400" />
             </div>
             <h3 className="text-xl font-medium text-gray-700 mb-2">No Applied Jobs</h3>
             <p className="text-gray-500 max-w-md mx-auto mb-6">
               You haven't applied to any jobs yet. Start by finding and saving jobs that match your profile.
             </p>
             <Button variant="default" className="gap-2" onClick={() => setActiveTab("search")}>
               Find Jobs <ArrowRight className="h-4 w-4" />
             </Button>
           </div>
         )}
       </TabsContent>
       
       {isPremium && (
         <TabsContent value="history" className="space-y-6">
           <Card>
             <CardHeader>
               <CardTitle className="text-lg font-medium text-gray-800">Search History</CardTitle>
               <CardDescription>View and repeat your previous job searches</CardDescription>
             </CardHeader>
             <CardContent>
               {(searchHistory?.length > 0) ? (
                 <ScrollArea className="h-[300px] rounded-md border p-4">
                   <div className="space-y-4">
                     {(searchHistory || Array(5).fill(0)).map((search, idx) => (
                       <div key={idx} className="border-b pb-3 last:border-0">
                         <div className="flex justify-between items-start">
                           <div>
                             <p className="font-medium">
                               {search?.query || 
                                ["React Developer", "Product Manager", "Data Scientist", "UX Designer", "Marketing Specialist"][idx]}
                             </p>
                             <p className="text-sm text-gray-500">
                               {search?.date || `February ${25 + idx}, 2025`} • 
                               {search?.results || Math.floor(Math.random() * 30) + 20} results
                             </p>
                           </div>
                           <Button 
                             variant="ghost" 
                             size="sm" 
                             onClick={() => handleFindMatches(search?.query || ["React Developer", "Product Manager", "Data Scientist", "UX Designer", "Marketing Specialist"][idx])}
                           >
                             Search Again
                           </Button>
                         </div>
                       </div>
                     ))}
                   </div>
                 </ScrollArea>
               ) : (
                 <div className="text-center py-8 text-gray-500">
                   <p>Your search history will appear here</p>
                 </div>
               )}
             </CardContent>
           </Card>
         </TabsContent>
       )}
     </Tabs>
     
     <Dialog open={showJobDetailsDialog} onOpenChange={setShowJobDetailsDialog}>
       <DialogContent className="max-w-3xl">
         <DialogHeader>
           <DialogTitle>
             {selectedJob?.title || "Senior Frontend Developer"} 
             {isPremium && <Badge className="ml-2 bg-gradient-to-r from-amber-200 to-amber-400 text-amber-900">
               92% Match
             </Badge>}
           </DialogTitle>
         </DialogHeader>
         
         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           <div className="md:col-span-2 space-y-6">
             <div className="flex flex-col sm:flex-row sm:items-center gap-4">
               <div className="flex items-center gap-3">
                 <div className="h-12 w-12 bg-gray-100 rounded-md flex items-center justify-center">
                   <Building2 className="h-6 w-6 text-gray-500" />
                 </div>
                 <div>
                   <h3 className="font-medium text-gray-900">{selectedJob?.company || "Google"}</h3>
                   <p className="text-sm text-gray-500">{selectedJob?.location || "San Francisco, CA"}</p>
                 </div>
               </div>
               
               <div className="flex gap-2 sm:ml-auto">
                 <Button variant="outline" size="sm" className="gap-1"
                   onClick={() => {
                     toggleSaveJob(selectedJob?.id || "job-123");
                     toast({
                       title: isSaved(selectedJob?.id || "job-123") ? "Job Removed" : "Job Saved",
                       description: isSaved(selectedJob?.id || "job-123") 
                         ? "This job has been removed from your saved jobs." 
                         : "This job has been added to your saved jobs."
                     });
                   }}
                 >
                   <Star className={`h-4 w-4 ${isSaved(selectedJob?.id || "job-123") ? "fill-amber-400 text-amber-400" : ""}`} />
                   {isSaved(selectedJob?.id || "job-123") ? "Saved" : "Save Job"}
                 </Button>
                 <Button variant="default" className="gap-1 bg-gradient-to-r from-blue-500 to-indigo-500"
                   onClick={() => {
                     setShowJobDetailsDialog(false);
                     toast({
                       title: "Application Started",
                       description: "You've started the application process for this job."
                     });
                   }}
                 >
                   Apply Now
                 </Button>
               </div>
             </div>
             
             <div className="space-y-4">
               <div className="flex flex-wrap gap-2">
                 <Badge variant="outline" className="bg-gray-100">Full-time</Badge>
                 <Badge variant="outline" className="bg-gray-100">Remote</Badge>
                 <Badge variant="outline" className="bg-gray-100">$120k - $150k</Badge>
                 <Badge variant="outline" className="bg-gray-100">Posted 2 days ago</Badge>
               </div>
               
               <div className="space-y-4">
                 <div>
                   <h3 className="text-lg font-medium text-gray-900 mb-2">Job Description</h3>
                   <p className="text-gray-700 leading-relaxed">
                     We are looking for a Senior Frontend Developer to join our team. You will be responsible for building and maintaining user interfaces for web applications. You will work closely with designers, backend developers, and product managers to deliver high-quality, responsive web applications.
                   </p>
                 </div>
                 
                 <div>
                   <h3 className="text-lg font-medium text-gray-900 mb-2">Requirements</h3>
                   <ul className="list-disc list-inside text-gray-700 space-y-1">
                     <li>5+ years of experience with React and modern JavaScript</li>
                     <li>Experience with TypeScript and state management libraries</li>
                     <li>Strong understanding of web fundamentals (HTML, CSS, accessibility)</li>
                     <li>Experience with responsive design and cross-browser compatibility</li>
                     <li>Experience with testing frameworks and CI/CD pipelines</li>
                   </ul>
                 </div>
                 
                 <div>
                   <h3 className="text-lg font-medium text-gray-900 mb-2">Benefits</h3>
                   <ul className="list-disc list-inside text-gray-700 space-y-1">
                     <li>Competitive salary and equity</li>
                     <li>Comprehensive health, dental, and vision insurance</li>
                     <li>Flexible work hours and remote work options</li>
                     <li>20 days of paid time off</li>
                     <li>401(k) matching and professional development budget</li>
                   </ul>
                 </div>
               </div>
             </div>
           </div>
           
           <div className="md:col-span-1 space-y-4">
             {isPremium && (
               <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-blue-100">
                 <CardHeader className="pb-2">
                   <CardTitle className="text-base text-blue-800">AI Match Analysis</CardTitle>
                 </CardHeader>
                 <CardContent className="space-y-3">
                   <div>
                     <h4 className="text-sm font-medium text-blue-700 mb-1">Match Score</h4>
                     <div className="flex items-center gap-2">
                       <Progress value={92} className="h-2" />
                       <span className="text-sm font-bold text-blue-800">92%</span>
                     </div>
                   </div>
                   
                   <div>
                     <h4 className="text-sm font-medium text-blue-700 mb-1">Skill Match</h4>
                     <div className="space-y-2">
                       {["React", "TypeScript", "JavaScript", "CSS"].map((skill, idx) => (
                         <div key={idx} className="flex items-center justify-between text-sm">
                           <span>{skill}</span>
                           <Badge className="bg-blue-100 text-blue-700">{90 - idx * 5}%</Badge>
                         </div>
                       ))}
                     </div>
                   </div>
                   
                   <div>
                     <h4 className="text-sm font-medium text-blue-700 mb-1">AI Insights</h4>
                     <p className="text-xs text-blue-600">
                       This job is an excellent match for your experience with frontend technologies. 
                       Your projects in React and TypeScript align well with the requirements.
                     </p>
                   </div>
                 </CardContent>
               </Card>
             )}
             
             <Card>
               <CardHeader className="pb-2">
                 <CardTitle className="text-base">Company Information</CardTitle>
               </CardHeader>
               <CardContent className="space-y-3">
                 <div className="flex items-center gap-2">
                   <Globe className="h-4 w-4 text-gray-500" />
                   <a href="#" className="text-sm text-blue-600 hover:underline">company-website.com</a>
                 </div>
                 <div className="flex items-center gap-2">
                   <Users className="h-4 w-4 text-gray-500" />
                   <span className="text-sm text-gray-600">1000-5000 employees</span>
                 </div>
                 <div className="flex items-center gap-2">
                   <Building2 className="h-4 w-4 text-gray-500" />
                   <span className="text-sm text-gray-600">Technology</span>
                 </div>
                 
                 <div className="pt-2">
                   <Button variant="outline" size="sm" className="w-full gap-1">
                     <MessageCircle className="h-4 w-4" /> Contact Recruiter
                   </Button>
                 </div>
               </CardContent>
             </Card>
             
             <Card>
               <CardHeader className="pb-2">
                 <CardTitle className="text-base">Similar Jobs</CardTitle>
               </CardHeader>
               <CardContent className="space-y-3 p-3">
                 {Array(3).fill(0).map((_, idx) => (
                   <div key={idx} className="p-2 hover:bg-gray-50 rounded-md cursor-pointer">
                     <p className="font-medium text-sm text-gray-900">
                       {["Frontend Developer", "React Developer", "UI Engineer"][idx]}
                     </p>
                     <p className="text-xs text-gray-500">
                       {["Microsoft", "Apple", "Amazon"][idx]} · {["Seattle", "Cupertino", "Remote"][idx]}
                     </p>
                   </div>
                 ))}
               </CardContent>
             </Card>
           </div>
         </div>
       </DialogContent>
     </Dialog>
     
     <div className="mt-8 relative rounded-3xl overflow-hidden">
       <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-sky-600" />
       <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1496307653780-42ee777d4833?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80')] mix-blend-overlay opacity-10" />
       <div className="relative z-10 text-center space-y-6 px-6 py-12">
         <h2 className="text-2xl md:text-3xl font-bold text-white">
           Ready to Find Your Dream Job?
         </h2>
         <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto">
           Join thousands of professionals who have already found their perfect career match using our AI-powered platform
         </p>
         <div className="flex flex-wrap gap-4 justify-center mt-4">
           <Button 
             size="lg"
             className="bg-white text-emerald-600 hover:bg-gray-100"
             onClick={() => document.querySelector('#search-form')?.scrollIntoView({ behavior: 'smooth' })}
           >
             Get Started Now <ArrowRight className="ml-2 h-5 w-5" />
           </Button>
           {!isPremium && (
             <Button 
               size="lg"
               variant="outline"
               className="border-white text-white hover:bg-white/10"
               onClick={() => toast({
                 title: "Premium Feature",
                 description: "Upgrade to Premium for enhanced AI job matching and higher match scores.",
               })}
             >
               Upgrade to Premium <Sparkles className="ml-2 h-5 w-5" />
             </Button>
           )}
         </div>
       </div>
     </div>
   </div>
 );
};

export default AIJobMatching;
